<template>
  <div>
    <text-input-fragment
        :label="getLabel('postal_code')"
        :placeholder="$t(i18n.placeholder.postal_code)"
        :is-invalid="isFieldInvalid($v.postalCode)"
        :disabled="isBusyFetchingAddress"
        v-debounce:1000ms="fetchAddress"
        v-model="postalCode"
        :margin-top="marginTop"
    >
      <template #result>
        <field-error-messages :validation-state="$v.postalCode"/>
      </template>
    </text-input-fragment>

    <text-input-fragment
        :label="getLabel('house_number')"
        :placeholder="$t(i18n.placeholder.house_number)"
        :is-invalid="isFieldInvalid($v.houseNumber)"
        :disabled="isBusyFetchingAddress"
        v-debounce:1000ms="fetchAddress"
        v-model="houseNumber"
    >
      <template #result>
        <field-error-messages :validation-state="$v.houseNumber"/>
      </template>
    </text-input-fragment>

    <text-input-fragment
        :label="getLabel('house_number_addition')"
        :placeholder="$t(i18n.placeholder.house_number_addition)"
        :is-invalid="isFieldInvalid($v.houseNumberAddition)"
        :disabled="isBusyFetchingAddress"
        v-debounce:1000ms="fetchAddress"
        v-model="houseNumberAddition"
    >
      <template #result>
        <field-error-messages :validation-state="$v.houseNumberAddition"/>
      </template>
    </text-input-fragment>

    <div v-if="addressFetchError" :class="$style.error">
      {{ $t(i18n.address_FETCH_ERROR) }}
    </div>

    <text-fragment v-if="!isBusyFetchingAddress && streetName"
                   :label="resultTitle">
      {{ streetName }} {{ houseNumber }}{{ houseNumberAddition }} <br>
      {{ postalCode }} {{ city }}
    </text-fragment>
  </div>
</template>

<script>
import {Utils} from "@bvm/core";
import {TextFragment, TextInputFragment} from "@bvm/forms";
import {postalCode} from "../../validations";
import {alpha, not, numeric, or, required} from 'vuelidate/lib/validators'
import {getAddress} from "../../api/address-api";

const i18nReusablesBuilder = new Utils.I18n.I18nBuilder("reusable")
    .add("label", a => a
        .add("postal_code")
        .add("house_number")
        .add("house_number_addition")
    )
    .add("placeholder", a => a
        .add("postal_code")
        .add("house_number")
        .add("house_number_addition")
    );

const i18nValidationBuilder = new Utils.I18n.I18nBuilder("validations_field")
    .add("postal")
    .add("alpha")
    .add("required")
    .add("address_FETCH_ERROR");

export const messageKeys = [...i18nReusablesBuilder.getKeys(), ...i18nValidationBuilder.getKeys()];

export default {
  name: "address-input",
  components: {
    "field-error-messages": () => import("./field-error-messages"),
    TextInputFragment,
    TextFragment
  },
  props: {
    // Add specific suffix to all generic field labels
    labelSuffix: {type: String},
    resultLabel: {type: String, required: true, default: ""},
    address: {type: Object},
    marginTop: {
      type: String,
      required: false,
      default: 'm',
      validator: function(value) {
        return ["s", "m", "l", "0"].indexOf(value) !== -1;
      }
    }
  },
  data() {
    return {
      i18n: {...i18nReusablesBuilder.getData(), ...i18nValidationBuilder.getData()},
      addressFetchError: null,
      isBusyFetchingAddress: false,
      streetName: null,
      houseNumber: null,
      houseNumberAddition: null,
      postalCode: null,
      city: null
    };
  },
  mounted() {
    this.copyAddressToFields(this.address);
  },
  watch: {
    address: {
      deep: true,
      handler(address) {
        this.copyAddressToFields(address);
      }
    }
  },
  computed: {
    // Capitalize first letter of 'resultLabel' property
    resultTitle() {
      if (this.resultLabel.length <= 1) {
        return this.resultLabel;
      }
      return this.resultLabel.charAt(0).toUpperCase() + this.resultLabel.slice(1);
    }
  },
  methods: {
    isFieldInvalid(field) {
      return field.$dirty && field.$invalid;
    },
    getLabel(labelKey) {
      let suffix = this.labelSuffix ? " " + this.labelSuffix : "";
      return this.$t(this.i18n.label[labelKey]) + suffix;
    },
    copyAddressToFields(address) {
      this.postalCode = address.postalCode;
      this.houseNumber = address.houseNumber;
      this.houseNumberAddition = address.houseNumberAddition;
    },
    fetchAddress() {
      // Reset old data
      this.addressFetchError = null;
      this.streetName = null;
      this.city = null;
      this.sendUpdateEvent();

      // Only try to fetch if all required fields are set and valid
      if (this.$v.postalCode.$invalid
          || this.$v.houseNumber.$invalid
          || this.$v.houseNumberAddition.$invalid) {
        return;
      }

      this.$v.$touch();
      this.isBusyFetchingAddress = true;

      getAddress(this.postalCode, this.houseNumber, this.houseNumberAddition)
          .then((resultAddress) => {
            if (!resultAddress) {
              this.addressFetchError = true;
              return;
            }

            this.streetName = resultAddress.streetName;
            this.city = resultAddress.city;
            this.sendUpdateEvent();
          })
          .catch(() => {
            this.addressFetchError = true;
          })
          .finally(() => {
            this.isBusyFetchingAddress = false;
          });
    },
    sendUpdateEvent() {
      this.address.streetName = this.streetName;
      this.address.houseNumber = this.houseNumber;
      this.address.houseNumberAddition = this.houseNumberAddition;
      this.address.postalCode = this.postalCode;
      this.address.city = this.city;
    }
  },
  validations() {
    return {
      postalCode: {
        required,
        "postal": postalCode
      },
      houseNumber: {
        required,
        numeric
      },
      houseNumberAddition: {
        alpha: or(alpha, not(required))
      }
    }
  }
};
</script>

<style module lang="scss">
//@import "~@bvm/forms/src/style/abstract/abstract.colors";
$color-attention: #dc143c;
$spacing-s: 15px;

.error {
  line-height: 1.3125;
  font-size: rem-calc(15);
  color: $color-attention;
  margin-top: rem-calc($spacing-s);
}
</style>
